import { useAuth } from "@/hooks/use-auth";
import { useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowRight, Lock } from "lucide-react";

export default function Login() {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      setLocation("/");
    }
  }, [isAuthenticated, isLoading, setLocation]);

  return (
    <div className="min-h-screen flex w-full">
      <div className="hidden lg:flex w-1/2 relative bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-slate-900/30 z-10" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#111827] via-[#1a2332] to-[#111827]" />
        <div className="relative z-20 flex flex-col justify-between p-16 h-full text-white">
          <div>
            <div className="bg-white rounded-lg px-3 py-2 inline-block">
              <img
                src="https://www.logrock.com/wp-content/uploads/2025/09/logrock-logo.png"
                alt="LogRock"
                className="h-8 object-contain"
              />
            </div>
          </div>
          <div />
          <div className="flex gap-4 text-sm text-slate-400 font-medium">
            <span>&copy; {new Date().getFullYear()} LogRock</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-md space-y-8 animate-in slide-in-from-right-8 duration-700">
          <div className="lg:hidden flex justify-center mb-8">
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border">
              <img 
                src="https://www.logrock.com/wp-content/uploads/2025/09/logrock-logo.png" 
                alt="LogRock" 
                className="h-8 object-contain"
              />
            </div>
          </div>

          <div className="text-center lg:text-left">
            <h1 className="text-2xl font-bold font-heading text-slate-900" data-testid="text-login-title">
              Welcome to LogRock
            </h1>
            <p className="text-muted-foreground mt-2">
              Sign in with your LogRock account to continue.
            </p>
          </div>

          <div className="space-y-4">
            <Button
              asChild
              className="w-full h-12 bg-[#111827] hover:bg-[#1a2332] text-white font-medium text-base transition-all shadow-lg"
              data-testid="button-login"
            >
              <a href="/api/login">
                <span className="flex items-center gap-2">
                  Sign In with Google <ArrowRight className="w-4 h-4" />
                </span>
              </a>
            </Button>
          </div>

          <div className="flex items-center gap-2 justify-center text-sm text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg p-3">
            <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span>Access restricted to <strong>@logrock.com</strong> accounts only</span>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Protected by LogRock Security
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
